import React, { useMemo, useState } from "react";

/**
 * Editor pour les restrictions HARD "surfaces par arbitre × jour".
 *
 * Props:
 *   officials         — liste complète des officiels (référee-only préféré)
 *   hiddenOfficialIds — set/array des IDs masqués (retirés de la liste)
 *   games             — parties de l'événement (pour extraire les dates)
 *   surfacesCount     — nombre de surfaces sur l'événement (numéros 1..N)
 *   value             — { oid: { "YYYY-MM-DD": Set<string> } }
 *   onChange(next)    — appelé avec le nouvel état
 *
 * UX: dropdown pour choisir un arbitre → grille dates × surfaces avec
 * cases à cocher. Vide (aucune surface cochée pour un jour) = pas de
 * restriction pour ce jour. On persiste explicitement au moment de
 * Recalculer/Appliquer via le parent.
 */
export default function RestrictionsEditor({
  officials = [],
  hiddenOfficialIds = [],
  games = [],
  surfacesCount = 0,
  value = {},
  onChange = () => {},
}) {
  const [selectedOid, setSelectedOid] = useState("");

  // Dates uniques triées à partir des parties.
  const dates = useMemo(() => {
    const s = new Set();
    for (const g of games || []) {
      if (g && !g.is_break && g.date) s.add(g.date);
    }
    return [...s].sort();
  }, [games]);

  // Officiels arbitres, non masqués.
  const hidden = new Set(hiddenOfficialIds || []);
  const refereeOfficials = useMemo(
    () =>
      (officials || [])
        .filter((o) => (o.roles || []).includes("referee"))
        .filter((o) => !hidden.has(o.id))
        .sort((a, b) =>
          `${a.first_name || ""} ${a.last_name || ""}`.localeCompare(
            `${b.first_name || ""} ${b.last_name || ""}`
          )
        ),
    [officials, hiddenOfficialIds] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const surfaces = useMemo(
    () => Array.from({ length: surfacesCount }, (_, i) => String(i + 1)),
    [surfacesCount]
  );

  const configuredCount = Object.keys(value || {}).length;

  const toggleSurface = (oid, date, surf) => {
    const next = { ...(value || {}) };
    const byDate = { ...(next[oid] || {}) };
    const setSurf = new Set(byDate[date] || []);
    if (setSurf.has(surf)) setSurf.delete(surf);
    else setSurf.add(surf);
    if (setSurf.size === 0) {
      delete byDate[date];
    } else {
      byDate[date] = setSurf;
    }
    if (Object.keys(byDate).length === 0) delete next[oid];
    else next[oid] = byDate;
    onChange(next);
  };

  const clearForRef = (oid) => {
    const next = { ...(value || {}) };
    delete next[oid];
    onChange(next);
  };

  const currentByDate = value?.[selectedOid] || {};

  if (dates.length === 0 || surfaces.length === 0) {
    return (
      <div className="text-[10px] text-[var(--muted)] font-mono">
        (Ajoute des parties à l&apos;événement pour configurer les restrictions.)
      </div>
    );
  }

  return (
    <div data-testid="restrictions-editor">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="label">
          Restrictions par arbitre × jour{" "}
          <span className="normal-case font-normal text-[var(--muted)]">
            (dures)
          </span>
          {configuredCount > 0 && (
            <span
              className="ml-2 inline-block px-2 py-0.5 text-[10px] rounded-full bg-black text-white font-mono"
              data-testid="restrictions-configured-count"
            >
              {configuredCount} arbitre{configuredCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>
      <div className="text-[10px] text-[var(--muted)] mb-2 font-mono leading-relaxed">
        Sélectionne un arbitre puis coche les surfaces autorisées pour chaque
        jour. Si aucune case n&apos;est cochée pour un jour, aucune
        restriction ne s&apos;applique ce jour-là.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={selectedOid}
          onChange={(e) => setSelectedOid(e.target.value)}
          className="input-box"
          data-testid="restrictions-official-picker"
        >
          <option value="">— Choisir un arbitre —</option>
          {refereeOfficials.map((o) => {
            const hasCfg = !!value?.[o.id];
            return (
              <option key={o.id} value={o.id}>
                {(o.first_name || "") + " " + (o.last_name || "")}
                {hasCfg ? " (configuré)" : ""}
              </option>
            );
          })}
        </select>
        {selectedOid && value?.[selectedOid] && (
          <button
            type="button"
            onClick={() => clearForRef(selectedOid)}
            className="text-xs px-2 py-1 rounded border border-[var(--border)] hover:border-black font-mono"
            data-testid="restrictions-clear-btn"
          >
            Réinitialiser cet arbitre
          </button>
        )}
      </div>
      {selectedOid && (
        <div
          className="overflow-x-auto rounded-lg border border-[var(--border)]"
          data-testid="restrictions-grid"
        >
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--surface-2)]">
                <th className="text-left px-2 py-1.5 font-mono font-normal border-b border-[var(--border)]">
                  Date
                </th>
                {surfaces.map((s) => (
                  <th
                    key={s}
                    className="px-2 py-1.5 font-mono font-normal border-b border-[var(--border)] text-center"
                  >
                    Surf {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((d) => {
                const set = currentByDate[d] || new Set();
                const cfgd = set.size > 0;
                return (
                  <tr key={d} className="hover:bg-[var(--surface-2)]">
                    <td className="px-2 py-1 font-mono border-b border-[var(--border)]">
                      {d}
                      {cfgd && (
                        <span className="ml-2 text-[10px] text-black/60">
                          ({set.size}/{surfaces.length})
                        </span>
                      )}
                    </td>
                    {surfaces.map((s) => {
                      const checked = set.has(s);
                      return (
                        <td
                          key={s}
                          className="px-2 py-1 border-b border-[var(--border)] text-center"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              toggleSurface(selectedOid, d, s)
                            }
                            className="cursor-pointer"
                            data-testid={`restriction-toggle-${d}-${s}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
